import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { HERO } from "@/data/content";
import { Container, Section } from "@/components/landing/shared";
import { usePrefersReducedMotion, EASE } from "@/lib/motion";

const HEADLINE_LINES = [HERO.headlineA, HERO.headlineB].filter(Boolean).map((line) => line.split(" "));

/** Fade/rise entrance used by every hero element, staggered by `delay`. */
const enter = (reduced, delay, y = 16) => ({
  initial: reduced ? false : { opacity: 0, y },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: EASE, delay },
});

const HeroHeadline = ({ reduced }) => {
  let wordIndex = 0;
  return (
    <h1
      className="font-display text-balance text-[2.5rem] leading-[1.02] tracking-[-0.03em] text-[color:var(--ink)] sm:text-[3.1rem] lg:text-[3.8rem] xl:text-[4.3rem]"
      data-testid="hero-headline"
    >
      {HEADLINE_LINES.map((words, lineIdx) => (
        <span key={words.join(" ")} className={lineIdx > 0 ? "mt-1 block" : "block"}>
          {words.map((word, i) => {
            const delay = 0.1 + wordIndex * 0.05;
            wordIndex += 1;
            return (
              <motion.span key={`${word}-${i}`} className="inline-block" {...enter(reduced, delay, 26)}>
                {word}
                {i < words.length - 1 ? "\u00A0" : ""}
              </motion.span>
            );
          })}
        </span>
      ))}
    </h1>
  );
};

export const Hero = () => {
  const reduced = usePrefersReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const copyY = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : 30]);

  return (
    <Section id="overview" navId="overview" className="hero-glow grain relative overflow-hidden pt-8 sm:pt-10 lg:pt-12">
      <div ref={ref} className="relative">
        <Container className="relative pb-8 lg:pb-10">
          <motion.div style={{ y: copyY }} className="relative z-10 max-w-[54rem]">
            <HeroHeadline reduced={reduced} />

            <motion.div className="mt-5 max-w-[40rem]" {...enter(reduced, 0.75)} data-testid="hero-support-copy">
              <p className="text-pretty text-[1.02rem] leading-[1.6] text-[color:var(--muted-ink)] sm:text-[1.08rem]">{HERO.support}</p>
              <p className="mt-4 text-pretty text-[1.02rem] leading-[1.6] text-[color:var(--muted-ink)] sm:text-[1.08rem]">
                {HERO.supportEmphasis}
                <strong className="font-bold italic text-[color:var(--ink)]">{HERO.supportEmphasisHighlight}</strong>
                {HERO.supportEmphasisTail}
              </p>
            </motion.div>
          </motion.div>
        </Container>
      </div>
    </Section>
  );
};
